// Техника упражнения: фото начала и конца движения, схема «Что качает», мышцы, оборудование, шаги.
// Без описания — кнопка «Написать с ИИ» (сохранит шаги в упражнение). Шторка «Инфо» — с экрана тренировки.

import { useMutation } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { useBodySex } from '@/api/settings'
import { MuscleLegend, MuscleMap } from '@/components/muscle-map/MuscleMap'
import { exerciseTones } from '@/components/muscle-map/muscles'
import { Button, ErrorText, Modal } from '@/components/ui'
import { useCharacter } from '@/pages/character/api'
import { useOnline } from '@/offline/queue'
import {
  exerciseImageUrl,
  techniqueSteps,
  trainingApi,
  useCatalogExercise,
  useInvalidateTraining,
  useMuscleLabels,
  type Exercise,
} from '@/pages/workouts/api'
import { EQUIPMENT_LABELS } from '@/pages/workouts/logic'

const PHOTO_CAPTIONS = ['Начало', 'Конец']

function Photos({ exercise }: { exercise: Exercise }) {
  if (exercise.images.length === 0) return null
  return (
    <div>
      <div className="-mx-1 flex snap-x snap-mandatory gap-2 overflow-x-auto px-1 pb-1">
        {exercise.images.map((name, i) => (
          <figure key={name} className="w-[82%] shrink-0 snap-start sm:w-[calc(50%-0.25rem)]">
            <img
              src={exerciseImageUrl(name)}
              alt={`${exercise.name}: ${PHOTO_CAPTIONS[i] ?? `фото ${i + 1}`}`}
              loading="lazy"
              className="aspect-[4/3] w-full rounded-lg bg-surface-2 object-cover"
            />
            <figcaption className="mt-1 text-xs text-muted">{PHOTO_CAPTIONS[i] ?? `Фото ${i + 1}`}</figcaption>
          </figure>
        ))}
      </div>
      <p className="mt-1 text-xs text-muted">
        Фото:{' '}
        <a href="https://github.com/yuhonas/free-exercise-db" target="_blank" rel="noreferrer" className="underline">
          free-exercise-db
        </a>
      </p>
    </div>
  )
}

const ROLE_TEXT = { primary: 'основная — упражнение на неё', secondary: 'помогает' } as const

/** Фигура с основной и вспомогательными мышцами; нажатие на мышцу подписывает её роль. */
function MusclesWorked({ exercise }: { exercise: Exercise }) {
  const sex = useBodySex()
  const muscle = useMuscleLabels()
  const [selected, setSelected] = useState<string | null>(null)
  const tones = useMemo(
    () => exerciseTones(exercise.primary_muscle, exercise.secondary_muscles),
    [exercise.primary_muscle, exercise.secondary_muscles],
  )
  const role = selected ? tones[selected] : undefined
  const names = [exercise.primary_muscle, ...exercise.secondary_muscles].map(muscle).join(', ')
  // Прокачка этих мышц у персонажа: «Грудь 5 ур.» с цветом ранга.
  const levels = useCharacter().data?.muscle_levels
  const leveled = levels
    ? [exercise.primary_muscle, ...exercise.secondary_muscles].filter((k, i, all) => levels[k] && all.indexOf(k) === i)
    : []
  return (
    <section aria-label="Что качает" className="flex flex-col gap-2">
      <h3 className="text-sm font-semibold text-fg">Что качает</h3>
      <MuscleMap
        sex={sex}
        tones={tones}
        selected={selected}
        onSelect={setSelected}
        label={`Работают: ${names}`}
        className="mx-auto w-full max-w-sm"
      />
      <p className="min-h-5 text-sm text-muted" aria-live="polite">
        {selected ? (
          <>
            <span className="font-medium text-fg">{muscle(selected)}</span> ·{' '}
            {role === 'primary' || role === 'secondary' ? ROLE_TEXT[role] : 'в упражнении не работает'}
          </>
        ) : (
          'Нажми на мышцу, чтобы узнать её роль'
        )}
      </p>
      <MuscleLegend tones={['primary', 'secondary']} />
      {leveled.length && levels ? (
        <p className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted" aria-label="Прокачка мышц">
          {leveled.map((k) => (
            <span key={k} className="inline-flex items-center gap-1.5">
              <span aria-hidden className="size-2.5 rounded-full" style={{ background: `var(--muscle-${levels[k]!.rank})` }} />
              <span className={k === exercise.primary_muscle ? 'font-medium text-fg' : undefined}>{muscle(k)}</span>
              {levels[k]!.level} ур.
            </span>
          ))}
        </p>
      ) : null}
    </section>
  )
}

function Steps({ exercise }: { exercise: Exercise }) {
  const online = useOnline()
  const invalidate = useInvalidateTraining()
  const describe = useMutation({
    mutationFn: () => trainingApi.describeExercise(exercise.id),
    onSuccess: () => invalidate(),
  })
  const steps = techniqueSteps(exercise.description)
  if (steps.length > 0) {
    return (
      <ol className="flex list-decimal flex-col gap-1.5 pl-5 text-sm text-fg marker:text-muted">
        {steps.map((step, i) => (
          <li key={i}>{step}</li>
        ))}
      </ol>
    )
  }
  return (
    <div className="flex flex-col items-start gap-2">
      <p className="text-sm text-muted">Техники пока нет.</p>
      <Button size="sm" icon="ai" loading={describe.isPending} disabled={!online} onClick={() => describe.mutate()}>
        Написать с ИИ
      </Button>
      {!online ? <p className="text-xs text-muted">Нужна связь</p> : null}
      <ErrorText error={describe.error} />
    </div>
  )
}

/** Фото, мышцы, оборудование и шаги техники — в шторке «Инфо» и на странице упражнения. */
export function ExerciseTechnique({ exercise, showMeta = true }: { exercise: Exercise; showMeta?: boolean }) {
  const muscle = useMuscleLabels()
  return (
    <div className="flex flex-col gap-4">
      <Photos exercise={exercise} />
      <MusclesWorked exercise={exercise} />
      {showMeta ? (
        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
          <dt className="text-muted">Мышцы</dt>
          <dd className="text-fg">
            {muscle(exercise.primary_muscle)}
            {exercise.secondary_muscles.length > 0 ? (
              <span className="text-muted"> · {exercise.secondary_muscles.map(muscle).join(', ')}</span>
            ) : null}
          </dd>
          <dt className="text-muted">Снаряд</dt>
          <dd className="text-fg">{EQUIPMENT_LABELS[exercise.equipment]}</dd>
        </dl>
      ) : null}
      <Steps exercise={exercise} />
      {exercise.notes ? <p className="rounded-lg bg-surface-2 p-3 text-sm text-fg">{exercise.notes}</p> : null}
    </div>
  )
}

/** Шторка «Инфо» с экрана тренировки и из справочника. */
export function ExerciseInfoSheet({
  exerciseId,
  name,
  open,
  onClose,
  exercise: given,
}: {
  exerciseId: string
  name: string
  open: boolean
  onClose: () => void
  /** Уже загруженное упражнение (из справочника с архивом); иначе — из каталога по id. */
  exercise?: Exercise
}) {
  const fromCatalog = useCatalogExercise(exerciseId)
  const exercise = given ?? fromCatalog
  return (
    <Modal open={open} onClose={onClose} title={name}>
      {exercise ? (
        <div className="flex flex-col gap-4">
          <ExerciseTechnique exercise={exercise} />
          <Link to={`/exercises/${exercise.id}`} className="text-sm text-muted hover:text-accent">
            Рекорды и история упражнения →
          </Link>
        </div>
      ) : (
        <p className="text-sm text-muted">Справочник ещё не загружен — открой, когда появится связь.</p>
      )}
    </Modal>
  )
}
